import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, ArrowRight, Check, Loader2, LockKeyhole, Mail, MailCheck, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiErrorMessage, errorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { AuthHeading, FieldError, FormAlert, IconInput } from './components/AuthFields'
import { AuthShell } from './components/AuthShell'

function Steps({ current }: { current: 1 | 2 }) {
  const { t } = useTranslation()
  const steps = [t('auth.steps.credentials'), t('auth.steps.verify')]
  return (
    <ol className="flex items-center gap-2" aria-label={t('auth.steps.label')}>
      {steps.map((label, i) => {
        const n = i + 1
        const done = n < current
        const active = n === current
        return (
          <li key={label} className="flex items-center gap-2" aria-current={active ? 'step' : undefined}>
            {i > 0 ? <span className={cn('h-px w-6', done || active ? 'bg-primary/50' : 'bg-border')} aria-hidden /> : null}
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full text-[11px] font-semibold transition-colors',
                done ? 'bg-success text-primary-foreground' : active ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground',
              )}
            >
              {done ? <Check className="size-3.5" strokeWidth={3} /> : n}
            </span>
            <span className={cn('text-xs font-medium', active ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
          </li>
        )
      })}
    </ol>
  )
}

export function LoginPage() {
  const { t } = useTranslation()
  const { status, startLogin, completeLogin, endedReason, user } = useAuth()
  const [step, setStep] = useState<{ pre: string; email: string } | null>(null)
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState<string | null>(endedReason ? errorMessage(endedReason) : null)

  const creds = useForm({ resolver: zodResolver(z.object({ email: z.email(t('auth.invalidEmail')), password: z.string().min(1, t('auth.passwordRequired')) })) })
  const code = useForm({ resolver: zodResolver(z.object({ otp: z.string().regex(/^\d{6}$/, t('auth.otpInvalid')) })) })

  if (status === 'authenticated') {
    if (user?.must_change_password) return <Navigate to="/change-password" replace />
    return <Navigate to="/" replace />
  }

  const fail = (e: unknown) => setError(apiErrorMessage(e))

  return (
    <AuthShell>
      <div className="space-y-7">
        <Steps current={step ? 2 : 1} />

        {step ? (
          <form className="space-y-5" onSubmit={code.handleSubmit(async (v) => { setError(null); try { await completeLogin(step.pre, v.otp, step.email, remember) } catch (e) { fail(e) } })}>
            <AuthHeading
              eyebrow={
                <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/15">
                  <MailCheck className="size-6" aria-hidden />
                </span>
              }
              title={t('auth.otpTitle')}
              subtitle={t('auth.otpSubtitle', { email: step.email })}
            />
            <div className="space-y-2">
              <Label htmlFor="otp">{t('auth.otp')}</Label>
              <Input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                placeholder="••••••"
                className="h-14 rounded-xl bg-muted/30 text-center font-mono text-2xl font-semibold tracking-[0.6em] dark:bg-muted/20"
                aria-invalid={!!code.formState.errors.otp}
                {...code.register('otp')}
              />
              <FieldError message={code.formState.errors.otp?.message} />
            </div>
            <label className="flex items-start gap-2.5 rounded-xl border bg-muted/30 p-3 text-sm dark:bg-muted/20">
              <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>
                <span className="font-medium">{t('auth.rememberDevice')}</span>
                <span className="block text-xs text-muted-foreground">{t('auth.rememberDeviceHint')}</span>
              </span>
            </label>
            {error ? <FormAlert>{error}</FormAlert> : null}
            <Button type="submit" className="h-11 w-full rounded-xl text-[15px]" disabled={code.formState.isSubmitting}>
              {code.formState.isSubmitting ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
              {t('auth.verify')}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => { setStep(null); setError(null) }}>
              <ArrowLeft />
              {t('auth.useOther')}
            </Button>
          </form>
        ) : (
          <form className="space-y-5" onSubmit={creds.handleSubmit(async (v) => { setError(null); try { const pre = await startLogin(v.email, v.password); if (pre) setStep({ pre, email: v.email }) } catch (e) { fail(e) } })}>
            <AuthHeading
              eyebrow={<p className="text-sm font-medium text-primary">{t('auth.welcome')}</p>}
              title={t('auth.title')}
              subtitle={t('auth.subtitle')}
            />
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <IconInput
                id="email"
                icon={Mail}
                type="email"
                autoComplete="username"
                autoFocus
                placeholder={t('auth.emailPlaceholder')}
                aria-invalid={!!creds.formState.errors.email}
                {...creds.register('email')}
              />
              <FieldError message={creds.formState.errors.email?.message} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
                  {t('auth.forgot')}
                </Link>
              </div>
              <IconInput
                id="password"
                icon={LockKeyhole}
                type="password"
                revealable
                autoComplete="current-password"
                placeholder="••••••••"
                aria-invalid={!!creds.formState.errors.password}
                {...creds.register('password')}
              />
              <FieldError message={creds.formState.errors.password?.message} />
            </div>
            {error ? <FormAlert>{error}</FormAlert> : null}
            <Button type="submit" className="group h-11 w-full rounded-xl text-[15px] shadow-[0_8px_20px_-8px_var(--color-primary)]" disabled={creds.formState.isSubmitting}>
              {creds.formState.isSubmitting ? <Loader2 className="animate-spin" /> : null}
              {t('auth.submit')}
              {creds.formState.isSubmitting ? null : <ArrowRight className="transition-transform group-hover:translate-x-0.5" />}
            </Button>
          </form>
        )}

        <div className="flex items-start gap-3 rounded-xl border bg-muted/40 p-3.5 dark:bg-muted/20">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-success/12 text-success">
            <ShieldCheck className="size-4" aria-hidden />
          </span>
          <p className="text-xs leading-relaxed text-muted-foreground">{t('auth.security')}</p>
        </div>
      </div>
    </AuthShell>
  )
}
